import { observer } from "mobx-react-lite";
import React, { FC, useState } from "react";

import {
  ActionButton,
  dataAttribute,
  DataAttributes,
  Heading,
  NoDataTile,
  Stack
} from "@bps/fluent-ui";
import {
  MedicalHistoryClinicalDataDto,
  MedicalHistoryClinicalDataItemDto,
  MedicalHistoryTestElements
} from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { usePatientRecordScreenContext } from "@modules/clinical/screens/context/PatientRecordScreenContext.ts";
import { ClinicalRecord } from "@stores/clinical/models/ClinicalRecord.ts";
import { User } from "@stores/core/models/User.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { withFetch } from "@ui-components/data-fetcher/DataFetcher.tsx";

import { DeleteMedicalHistoryDialog } from "./DeleteMedicalHistoryDialog.tsx";
import { MedicalHistoryDataFetcher } from "./MedicalHistoryDataFetcher.tsx";
import { MedicalHistoryFilter } from "./MedicalHistoryFilters.tsx";
import { MedicalHistoryFormDialog } from "./MedicalHistoryFormDialog.tsx";
import { MedicalHistoryList } from "./MedicalHistoryList.tsx";
import { deleteMedicalHistoryItem } from "./utils.ts";

interface MedicalHistoryProps {
  clinicalRecord: ClinicalRecord;
}

interface MedicalHistoryBaseProps extends MedicalHistoryProps {
  users: User[];
}

const MedicalHistoryBase: FC<MedicalHistoryBaseProps> = observer(
  ({ clinicalRecord, users }) => {
    const { core } = useStores();

    let medicalHistories =
      clinicalRecord.clinicalData?.medicalHistory?.medicalHistories ?? [];

    //Confidential feature toggle
    if (!core.hasPermissions(Permission.ConfidentialDataAllowed)) {
      medicalHistories = medicalHistories.filter(x => !x.secGroupId);
    }

    const [isMedicalHistoryDialogVisible, setIsMedicalHistoryDialogVisible] =
      useState<boolean>(false);

    const [isDeleteDialogVisible, setIsDeleteDialogVisible] =
      useState<boolean>(false);

    const [selectedDiagnosis, setSelectedDiagnosis] = useState<
      MedicalHistoryClinicalDataItemDto | undefined
    >();

    const openMedicalHistoryDialog = (
      item?: MedicalHistoryClinicalDataItemDto
    ) => {
      setSelectedDiagnosis(item);
      setIsMedicalHistoryDialogVisible(true);
    };

    const closeMedicalHistoryDialog = () => {
      setSelectedDiagnosis(undefined);
      setIsMedicalHistoryDialogVisible(false);
    };

    const onDeleteCancel = () => {
      setIsDeleteDialogVisible(false);
    };

    const onDeleteDiagnosis = (item: MedicalHistoryClinicalDataItemDto) => {
      setSelectedDiagnosis(item);
      setIsDeleteDialogVisible(true);
    };

    const { isViewOnlyOrDischarged } = usePatientRecordScreenContext();

    const onDeleteConfirm = async (
      reasonForDelete: string,
      reasonForDeleteComment?: string
    ) => {
      if (selectedDiagnosis?.id) {
        const clinicalData = await deleteMedicalHistoryItem({
          clinicalRecord,
          id: selectedDiagnosis?.id,
          reasonForDelete,
          deletedComment: reasonForDeleteComment
        });
        await clinicalRecord.saveClinicalData(clinicalData);
        onDeleteCancel();
      }
    };

    const onSignificantMedicalHistoryChange = async () => {
      const medicalHistory: MedicalHistoryClinicalDataDto = {
        eTag: clinicalRecord.clinicalData?.medicalHistory?.eTag,
        noSignificantHistory: true,
        medicalHistories: []
      };

      await clinicalRecord.saveClinicalData({
        medicalHistory
      });
    };

    const hasNoSignificantHistory =
      clinicalRecord.clinicalData?.medicalHistory?.noSignificantHistory;

    const isEmptyList = !medicalHistories.length;

    const isExistConfidentialHxOfOthers = !!medicalHistories.find(
      x => x.secGroupId && !core.hasAccessToSecGroup(x.secGroupId)
    );

    return (
      <>
        <Stack
          horizontal
          horizontalAlign="space-between"
          verticalAlign="center"
        >
          <Heading
            variant="section-heading-light"
            styles={{ root: { padding: "5px 0" } }}
          >
            Medical history
          </Heading>

          {!isViewOnlyOrDischarged && (
            <ActionButton
              text="Add Medical Hx"
              iconProps={{
                iconName: "Add"
              }}
              onClick={() => openMedicalHistoryDialog()}
              styles={{ root: { alignSelf: "flex-start" } }}
            />
          )}
        </Stack>
        {!isViewOnlyOrDischarged && isEmptyList && !hasNoSignificantHistory && (
          <ActionButton
            {...dataAttribute(
              DataAttributes.Element,
              MedicalHistoryTestElements.NoHistoryButton
            )}
            styles={{ root: { alignSelf: "flex-start" } }}
            text="No significant history"
            iconProps={{
              iconName: "Blocked12"
            }}
            onClick={onSignificantMedicalHistoryChange}
          />
        )}

        {!isEmptyList && (
          <MedicalHistoryFilter
            clinicalRecord={clinicalRecord}
            isExistConfidentialHx={isExistConfidentialHxOfOthers}
          >
            {({ values }, actions) => {
              if (!isExistConfidentialHxOfOthers && !!values.confidential) {
                actions.setValue("confidential", false);
              }
              return (
                <MedicalHistoryList
                  clinicalRecord={clinicalRecord}
                  filter={values}
                  onDeleteDiagnosis={onDeleteDiagnosis}
                  openMedicalHistoryDialog={openMedicalHistoryDialog}
                  users={users}
                />
              );
            }}
          </MedicalHistoryFilter>
        )}

        {isEmptyList && (
          <NoDataTile
            styles={{ root: { height: 378 } }}
            textProps={{
              text: hasNoSignificantHistory
                ? "No significant medical history recorded"
                : "No medical history entries"
            }}
            linkProps={{ hidden: true }}
            showBoxShadow={false}
          />
        )}

        <MedicalHistoryFormDialog
          hidden={!isMedicalHistoryDialogVisible}
          selectedDiagnosis={selectedDiagnosis}
          onDismiss={closeMedicalHistoryDialog}
          medicalHistories={medicalHistories}
        />

        <DeleteMedicalHistoryDialog
          hidden={!isDeleteDialogVisible}
          onConfirm={onDeleteConfirm}
          onCancel={onDeleteCancel}
          item={selectedDiagnosis}
        />
      </>
    );
  }
);

const MedicalHistoryMiddle: React.FC<MedicalHistoryProps> = props => {
  return (
    <MedicalHistoryDataFetcher clinicalRecord={props.clinicalRecord}>
      {users => <MedicalHistoryBase users={users} {...props} />}
    </MedicalHistoryDataFetcher>
  );
};

export const MedicalHistory = withFetch(
  x => [x.clinical.ref.sidesOfBody.load()],
  MedicalHistoryMiddle
);
